import { ChangeEvent, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { ReceivableChanges } from './useReceivablePersistence';
import { useOptimisticReceivable } from './useOptimisticReceivable';

type CheckboxKey = 'supplyProIntegrated' | 'billed';

/**
 * Optimistic persistence for a single boolean receivable column, layered on
 * useOptimisticReceivable. The displayed value is owned locally; re-toggling is
 * always allowed and just fires another save (matching the other sheet checkbox
 * fields), and the value reverts on failure.
 */
export const useReceivableCheckboxField = (
  record: JobPhaseReceivableSheetInterface | undefined,
  field: CheckboxKey,
): {
  checked: boolean;
  receivableId: string | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
} => {
  const notify = useNotify();
  const { receivableId, pendingRef, commit } = useOptimisticReceivable(record);
  const [checked, setChecked] = useState<boolean>(!!record?.[field]);

  useEffect(() => {
    // Don't clobber an in-flight optimistic value.
    if (pendingRef.current) return;
    setChecked(!!record?.[field]);
  }, [record, field, pendingRef]);

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!record) return;
    const next = event.target.checked;
    const changes: ReceivableChanges = { [field]: next };

    setChecked(next);
    try {
      await commit(changes);
      notify('Changes were made successfully!');
    } catch (error) {
      setChecked(!next);
      const message =
        error instanceof Error ? error.message : 'An error has occurred!';
      notify(message, 'warning');
    }
  };

  return { checked, receivableId, onChange };
};
